import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Check, Clock, Lock, PlayCircle, ShieldCheck } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { coursesApi, formatApiErrorDetail } from "@/lib/api";
import { formatDuration } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import PageSEO from "@/components/seo/PageSEO";
import { SITE_BASE_URL, ORG_ID } from "@/components/seo/schemaHelpers";

export default function CourseDetailPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [enrolling, setEnrolling] = useState(false);

    useEffect(() => {
        setLoading(true);
        coursesApi
            .get(slug)
            .then(setCourse)
            .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "unknown"))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, isAuthenticated, user?.id]);

    const startLearning = (lessonId) =>
        navigate(`/portal/learn/${slug}/${lessonId}`);

    const handleEnroll = async () => {
        if (!isAuthenticated) {
            navigate("/portal/login", {
                state: { from: `/portal/courses/${slug}` },
            });
            return;
        }
        setEnrolling(true);
        try {
            await coursesApi.enroll(slug);
            toast.success("Enrolled. Let's get started.");
            const refreshed = await coursesApi.get(slug);
            setCourse(refreshed);
            const first = refreshed.lessons?.[0];
            if (first) startLearning(first.id);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setEnrolling(false);
        }
    };

    if (loading) {
        return (
            <div
                className="container-x py-40 font-mono-tag text-brand-steel"
                data-testid="course-loading"
            >
                LOADING COURSE…
            </div>
        );
    }

    if (error === "not_found") {
        return (
            <div className="container-x py-40" data-testid="course-not-found">
                <p className="font-mono-tag text-brand-gold">[ 404 ]</p>
                <h1 className="mt-6 font-display text-6xl">COURSE NOT FOUND</h1>
                <Link
                    to="/portal/courses"
                    className="mt-8 inline-block font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal hover:text-brand-gold"
                >
                    ← Back to catalog
                </Link>
            </div>
        );
    }

    if (!course) return null;

    const nextLesson =
        course.lessons.find((l) => !l.completed) || course.lessons[0];

    return (
        <div className="bg-brand-offwhite" data-testid="course-detail-page">
            <PageSEO
                title={`${course.title} — ${course.category} Course | EB Construction Academy`}
                description={course.short_description}
                canonicalPath={`/portal/courses/${course.slug}`}
                ogType="article"
                ogImage={
                    course.cover_image?.startsWith("http")
                        ? course.cover_image
                        : `${SITE_BASE_URL}${course.cover_image || ""}`
                }
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Course",
                    name: course.title,
                    description: course.long_description || course.short_description,
                    url: `${SITE_BASE_URL}/portal/courses/${course.slug}`,
                    provider: {
                        "@type": "EducationalOrganization",
                        name: "EB Construction Training Academy",
                        sameAs: `${SITE_BASE_URL}/portal`,
                        "@id": ORG_ID,
                    },
                    educationalLevel: course.level,
                    about: course.category,
                    inLanguage: "en-GB",
                    image: course.cover_image?.startsWith("http")
                        ? course.cover_image
                        : `${SITE_BASE_URL}${course.cover_image || "/brand/logo-primary.png"}`,
                    ...(course.instructor
                        ? {
                              instructor: {
                                  "@type": "Person",
                                  name: course.instructor.name,
                                  jobTitle: course.instructor.title,
                              },
                          }
                        : {}),
                    hasCourseInstance: [
                        {
                            "@type": "CourseInstance",
                            courseMode: "Online",
                            courseWorkload: `PT${Math.round(course.duration_minutes / 60)}H`,
                            inLanguage: "en-GB",
                        },
                    ],
                    offers: {
                        "@type": "Offer",
                        price: "0",
                        priceCurrency: "GBP",
                        availability: "https://schema.org/InStock",
                        category: "Free",
                        url: `${SITE_BASE_URL}/portal/courses/${course.slug}`,
                    },
                }}
            />
            {/* Hero */}
            <section className="relative border-b border-brand-line">
                <div className="relative aspect-[16/8] overflow-hidden">
                    <img
                        src={course.cover_image}
                        alt={course.title}
                        className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal via-brand-charcoal/40 to-transparent" />
                    <div className="container-x absolute inset-x-0 bottom-0 pb-12 text-brand-offwhite">
                        <Reveal>
                            <div className="flex items-center gap-3">
                                <span className="border border-brand-gold bg-brand-gold px-2 py-1 font-mono-tag text-brand-charcoal">
                                    {course.category}
                                </span>
                                <span className="font-mono-tag text-brand-offwhite/80">
                                    {course.level}
                                </span>
                                <span className="font-mono-tag text-brand-offwhite/80">
                                    · {Math.round(course.duration_minutes / 60)}h
                                </span>
                            </div>
                            <h1 className="mt-4 font-display text-6xl leading-[1.05] md:text-8xl">
                                {course.title}
                            </h1>
                            <p className="mt-4 max-w-2xl font-body text-lg text-brand-offwhite/85">
                                {course.short_description}
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-b border-brand-line py-20 md:py-24">
                <div className="container-x grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-7">
                        <Reveal>
                            <p className="font-mono-tag text-brand-gold">
                                [ Overview ]
                            </p>
                            <p className="mt-4 font-body text-lg leading-relaxed text-brand-charcoal">
                                {course.long_description}
                            </p>
                        </Reveal>

                        <Reveal delay={0.1} className="mt-10">
                            <p className="font-mono-tag text-brand-gold">
                                [ Learning Outcomes ]
                            </p>
                            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {course.learning_outcomes.map((o) => (
                                    <li
                                        key={o}
                                        className="flex items-start gap-3 border-l-2 border-brand-gold bg-white p-4"
                                        data-testid={`course-outcome-${o.slice(0, 20).toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                                    >
                                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />
                                        <span className="font-body text-sm text-brand-charcoal">
                                            {o}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>

                        <Reveal delay={0.15} className="mt-12">
                            <p className="font-mono-tag text-brand-gold">
                                [ Instructor ]
                            </p>
                            <div className="mt-4 flex items-start gap-6 border border-brand-line bg-white p-6">
                                <img
                                    src={course.instructor.avatar}
                                    alt={course.instructor.name}
                                    className="h-20 w-20 object-cover"
                                />
                                <div>
                                    <h4 className="font-display text-2xl font-semibold leading-[1.15]">
                                        {course.instructor.name}
                                    </h4>
                                    <p className="mt-1 font-mono-tag text-brand-gold">
                                        {course.instructor.title}
                                    </p>
                                    <p className="mt-3 font-body text-sm leading-relaxed text-brand-steel">
                                        {course.instructor.bio}
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <aside className="md:col-span-5">
                        <Reveal delay={0.1}>
                            <div className="border border-brand-line bg-white p-6">
                                <p className="font-mono-tag text-brand-gold">
                                    [ Lessons ]
                                </p>
                                <ul className="mt-4 divide-y divide-brand-line">
                                    {course.lessons.map((l) => {
                                        const locked = !course.is_enrolled && !l.is_free;
                                        return (
                                            <li
                                                key={l.id}
                                                className="py-4"
                                                data-testid={`course-lesson-${l.id}`}
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-start gap-3">
                                                        <span className="mt-0.5 font-mono-tag text-brand-steel">
                                                            {String(l.order).padStart(2, "0")}
                                                        </span>
                                                        <div>
                                                            <p className="font-body font-medium text-brand-charcoal">
                                                                {l.title}
                                                            </p>
                                                            <p className="mt-1 flex items-center gap-3 font-mono-tag text-brand-steel">
                                                                <Clock className="h-3 w-3" />
                                                                {l.duration_minutes} min
                                                                {l.is_free && (
                                                                    <span className="text-brand-gold">
                                                                        · FREE PREVIEW
                                                                    </span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {l.completed ? (
                                                        <Check
                                                            className="mt-1 h-5 w-5 text-brand-gold"
                                                            aria-label="Completed"
                                                        />
                                                    ) : locked ? (
                                                        <Lock
                                                            className="mt-1 h-5 w-5 text-brand-steel/60"
                                                            aria-label="Locked"
                                                        />
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => startLearning(l.id)}
                                                            className="mt-1 grid h-5 w-5 place-items-center text-brand-charcoal transition-colors hover:text-brand-gold"
                                                            aria-label={`Open lesson ${l.title}`}
                                                            data-testid={`course-lesson-play-${l.id}`}
                                                        >
                                                            <PlayCircle className="h-5 w-5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="mt-6 border-t border-brand-line pt-6">
                                    {course.is_enrolled ? (
                                        <button
                                            type="button"
                                            onClick={() => startLearning(nextLesson.id)}
                                            className="btn-primary w-full"
                                            data-testid="course-continue-button"
                                        >
                                            Continue learning
                                            <ArrowUpRight className="h-4 w-4" />
                                        </button>
                                    ) : isAuthenticated ? (
                                        <button
                                            type="button"
                                            onClick={handleEnroll}
                                            disabled={enrolling}
                                            className="btn-primary w-full disabled:opacity-60"
                                            data-testid="course-enroll-button"
                                        >
                                            {enrolling ? "Enrolling…" : "Enroll now"}
                                            <ArrowUpRight className="h-4 w-4" />
                                        </button>
                                    ) : (
                                        <Link
                                            to="/portal/login"
                                            state={{ from: `/portal/courses/${slug}` }}
                                            className="btn-primary w-full"
                                            data-testid="course-login-to-enroll"
                                        >
                                            Log in to enroll
                                            <ArrowUpRight className="h-4 w-4" />
                                        </Link>
                                    )}
                                    <p className="mt-4 flex items-center gap-2 font-mono-tag text-brand-steel">
                                        <ShieldCheck className="h-3 w-3 text-brand-gold" />
                                        Full access with a free account
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </aside>
                </div>
            </section>
        </div>
    );
}
